import type { CourseQuizContent } from '../types'

export const aiFundamentalsContent: CourseQuizContent = {
  courseSlug: 'ai-fundamentals-think-create-and-work-smarter',
  courseName: 'AI Fundamentals',

  goalQuestion: {
    title: 'How would you describe your current experience with AI tools?',
    options: [
      { label: 'I haven’t really started yet.', value: 'not-started' },
      {
        label: 'I’ve tried a few tools, but I’m not getting much out of them.',
        value: 'few-tools',
      },
      {
        label: 'I use AI regularly and want to go deeper.',
        value: 'go-deeper',
      },
    ],
  },

  levelQuestion: {
    title: 'Right now, I want to…',
    options: [
      {
        label: 'Work faster and spend less time on routine tasks.',
        value: 'work-faster',
      },
      {
        label: 'Think better and make sharper decisions.',
        value: 'think-better',
      },
      {
        label: 'Stay relevant as my industry changes.',
        value: 'stay-relevant',
      },
      {
        label: 'Lead change and help my team adopt AI.',
        value: 'lead-change',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'AI changes every month. Build frameworks that will withstand the changes.',
    body: 'Master prompting, verification, tool evaluation, and a portfolio-ready AI Integration Framework — built to work on any tool, including ones that don’t exist yet.',
    image: 'https://static.masterclass.com/aifun-interstitial-1.png',
    imageAlt: 'The AI Integration Framework course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline: 'Designed for professionals at every stage.',
    body: 'No technical background required. Whether you’ve never touched an AI tool or use one every day, the course meets you where you are — and moves you significantly further.',
    image: 'https://static.masterclass.com/aifun-interstitial-2.png',
    imageAlt: 'A professional working with AI on a laptop',
  },

  profiles: [
    {
      key: 'work-faster',
      headline: 'You want to spend your time doing more meaningful work',
      lead: 'You’re spending most of your time doing mundane tasks that take away from higher-impact work. You need a system that will free up your time. This course helps you build one.',
      bullets: [
        {
          text: '**Build systems** that can handle drafting, research, and summarization, with AI doing the first pass.',
        },
        {
          text: 'Learn the **prompting frameworks** that make AI tools more useful.',
        },
        {
          text: 'Finish with a **repeatable AI workflow** you can use the day you complete the course.',
        },
      ],
    },
    {
      key: 'think-better',
      headline: 'You want to think better and make sharper calls with AI',
      lead: 'You care less about improving speed, and more about better judgment. This course trains you to use AI as a thought partner: stronger analysis, better questions, and the judgment to know when to trust it.',
      bullets: [
        {
          text: 'Use AI as a **thinking partner** for analysis, synthesis, and pressure-testing your ideas.',
        },
        {
          text: 'Learn to **ask better questions** so AI gives you better answers.',
        },
        {
          text: 'Build the **strategic instincts** to know when to trust AI output, and when to override it.',
        },
      ],
    },
    {
      key: 'stay-relevant',
      headline: 'You want to stay relevant and get ahead of the shift',
      lead: 'Your industry is changing fast, and it’s hard to feel confident in this shifting environment. This course gives you a clear map of what’s changing and a plan for your next move.',
      bullets: [
        {
          text: 'Get a **clear map of how AI is transforming your industry**: no hype, no fear, just what matters.',
        },
        {
          text: '**Identify which parts of your role are at risk** and which become more valuable with AI.',
        },
        {
          text: '**Build a personal AI strategy** so you’re ahead of the change, not reacting to it.',
        },
      ],
    },
    {
      key: 'lead-change',
      headline: 'You want to lead change and make AI work for your team',
      lead: 'Your team is looking for someone to make AI practical for them, and that someone is you. This course helps you own that conversation with confidence.',
      bullets: [
        {
          text: 'Get the **language and frameworks** to drive AI adoption and handle pushback.',
        },
        {
          text: '**Understand what changes for your team**, and what needs to be protected.',
        },
        {
          text: 'Learn to **build a culture** where humans and AI work better together.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '10 to 12 weeks' },
    { key: '1to3h', weeks: '4 to 6 weeks' },
    { key: '3to5h', weeks: '2 to 3 weeks' },
    { key: '5plush', weeks: '1 to 2 weeks' },
  ],

  previewLessons: [
    {
      title: 'AI as Your Copilot',
      mediaUuid: 'e5d6b82f-0660-4742-82a0-af1f8955ecc9',
      image: 'https://static.masterclass.com/aifun-video-1.png',
    },
    {
      title: 'Prompting: Speak So AI Listens',
      mediaUuid: '4b7a66a5-01ba-4fe4-94b1-b4d9f8eebbb7',
      image: 'https://static.masterclass.com/aifun-video-2.png',
    },
  ],

  ctaHighlights: [
    'Taught by leading AI experts and practitioners.',
    'Create your AI Integration Framework and 30–90-day plan.',
    'Earn a shareable MasterClass Certificate.',
  ],
}
